import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Calculator,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  CreditCard,
  Clock,
  CheckCircle2,
  XCircle,
  Info,
  Building2,
  ChevronRight,
  PiggyBank,
  Wallet,
  MessageCircle,
  QrCode,
  FileText,
  Smartphone,
  Car,
  Banknote,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_digital-bank-builder/artifacts/yj9xruy7_WhatsApp%20Image%202026-03-17%20at%2012.24.28.jpeg";
const WHATSAPP_LINK = "https://wa.me/5511921780586?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20a%20viabilidade%20da%20minha%20Fintech.";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('pt-BR').format(value);
};

const InputField = ({ label, value, onChange, min = 0, max = 100000, step = 1, prefix = "", suffix = "", tooltip = "", testId }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="text-slate-300 font-medium text-sm">{label}</Label>
      {tooltip && (
        <div className="group relative">
          <Info className="w-4 h-4 text-slate-500 cursor-help" />
          <div className="absolute right-0 bottom-full mb-2 w-56 p-2 bg-zinc-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            {tooltip}
          </div>
        </div>
      )}
    </div>
    <div className="flex items-center gap-2">
      {prefix && <span className="text-slate-500 text-sm">{prefix}</span>}
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        min={min}
        max={max}
        step={step}
        className="bg-zinc-800 border-white/10 text-white focus:bg-zinc-700"
        data-testid={testId}
      />
      {suffix && <span className="text-slate-500 text-sm">{suffix}</span>}
    </div>
    <Slider
      value={[value]}
      onValueChange={(v) => onChange(v[0])}
      min={min}
      max={max}
      step={step}
      className="mt-2"
    />
  </div>
);

const ResultCard = ({ icon: Icon, label, value, color = "slate", positive = null, testId }) => (
  <div className={`p-4 rounded-xl border ${color === 'green' ? 'bg-green-900/30 border-green-700/50' : color === 'red' ? 'bg-red-900/30 border-red-700/50' : 'bg-zinc-800 border-white/10'}`} data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color === 'green' ? 'bg-green-800/50' : color === 'red' ? 'bg-red-800/50' : 'bg-zinc-700'}`}>
        <Icon className={`w-5 h-5 ${color === 'green' ? 'text-green-400' : color === 'red' ? 'text-red-400' : 'text-slate-400'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 mb-0.5">{label}</p>
        <p className={`font-bold text-lg truncate ${color === 'green' ? 'text-green-400' : color === 'red' ? 'text-red-400' : 'text-white'}`}>
          {value}
        </p>
      </div>
      {positive !== null && (
        positive ? <TrendingUp className="w-5 h-5 text-green-500" /> : <TrendingDown className="w-5 h-5 text-red-500" />
      )}
    </div>
  </div>
);

export default function ViabilitySimulator() {
  // State baseado na planilha
  const [inputs, setInputs] = useState({
    // Contas
    contas_pf: 5000,
    contas_pj: 3000,
    custo_kyc_pf: 5,
    custo_kyc_pj: 10,
    spread_conta: 0.5,
    
    // Boletos
    boletos_codigo_barras: 50000,
    custo_boleto_codigo: 0.75,
    boletos_qrcode_pix: 25000,
    custo_boleto_qrcode: 0.25,
    spread_boleto: 0.5,
    
    // Pagamentos Boletos (rebate)
    pagamentos_boletos: 60000,
    rebate_boleto: 0.35,
    
    // Correspondente Bancário
    correspondente_fisico: 10000,
    rebate_correspondente: 1.35,
    correspondente_internet: 10000,
    rebate_internet: 0.05,
    
    // Débitos Veiculares
    debitos_veiculares: 50000,
    tarifa_debito_veicular: 1.0,
    spread_veicular: 0.5,
    
    // PIX
    pix_in: 1000000,
    custo_pix_in: 0.10,
    spread_pix_in: 0.05,
    pix_out: 1000000,
    custo_pix_out: 0.10,
    spread_pix_out: 0.05,
    
    // TED
    ted_in: 20000,
    custo_ted_in: 0.60,
    spread_ted_in: 0.20,
    ted_out: 20000,
    custo_ted_out: 0.60,
    spread_ted_out: 0.20,
    
    // Recarga Celular
    volume_recarga: 50000,
    rebate_recarga: 0.03,
    
    // Cartões
    transacoes_cartao: 1000000,
    mdr_percentual: 1.15,
    cartoes_ativos: 5000,
    mensalidade_cartao_ativo: 1.99,
    mensalidade_cartao_inativo: 0.99,
    spread_cartao: 0.5,
    
    // Float / Saldo
    saldo_medio_parado: 20000000,
    percentual_cdi: 0.9,
    
    // Setup e Mensalidade Provedor
    setup_provedor: 30000,
    mensalidade_provedor: 10000,
    
    // Crédito
    estimativa_credito: 5000000,
    spread_credito: 0.2,
  });

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateViability = useCallback(() => {
    setLoading(true);
    
    // Cálculos baseados na planilha
    const custos = {
      kyc_pf: inputs.contas_pf * inputs.custo_kyc_pf,
      kyc_pj: inputs.contas_pj * inputs.custo_kyc_pj,
      boletos_codigo: inputs.boletos_codigo_barras * inputs.custo_boleto_codigo,
      boletos_qrcode: inputs.boletos_qrcode_pix * inputs.custo_boleto_qrcode,
      debitos_veiculares: inputs.debitos_veiculares * inputs.tarifa_debito_veicular,
      pix_in: inputs.pix_in * inputs.custo_pix_in,
      pix_out: inputs.pix_out * inputs.custo_pix_out,
      ted_in: inputs.ted_in * inputs.custo_ted_in,
      ted_out: inputs.ted_out * inputs.custo_ted_out,
      cartoes_ativos: inputs.cartoes_ativos * inputs.mensalidade_cartao_ativo,
      cartoes_inativos: inputs.cartoes_ativos * inputs.mensalidade_cartao_inativo,
      setup_mensal: inputs.setup_provedor / 12,
      mensalidade: inputs.mensalidade_provedor,
    };
    
    const receitas = {
      contas_pf: inputs.contas_pf * inputs.spread_conta,
      contas_pj: inputs.contas_pj * inputs.spread_conta,
      kyc_pf: inputs.contas_pf * inputs.spread_conta,
      kyc_pj: inputs.contas_pj * inputs.spread_conta,
      boletos_codigo: inputs.boletos_codigo_barras * inputs.spread_boleto,
      boletos_qrcode: inputs.boletos_qrcode_pix * inputs.spread_boleto,
      pagamentos_boletos: inputs.pagamentos_boletos * inputs.rebate_boleto,
      correspondente_fisico: inputs.correspondente_fisico * inputs.rebate_correspondente,
      correspondente_internet: inputs.correspondente_internet * inputs.rebate_internet,
      debitos_veiculares: inputs.debitos_veiculares * inputs.spread_veicular,
      pix_in: inputs.pix_in * inputs.spread_pix_in,
      pix_out: inputs.pix_out * inputs.spread_pix_out,
      ted_in: inputs.ted_in * inputs.spread_ted_in,
      ted_out: inputs.ted_out * inputs.spread_ted_out,
      recarga: inputs.volume_recarga * inputs.rebate_recarga,
      mdr_cartao: inputs.transacoes_cartao * (inputs.mdr_percentual / 100),
      cartoes_ativos: inputs.cartoes_ativos * inputs.spread_cartao,
      cartoes_inativos: inputs.cartoes_ativos * inputs.spread_cartao,
      float_cdi: inputs.saldo_medio_parado * (inputs.percentual_cdi / 100),
      credito: inputs.estimativa_credito * (inputs.spread_credito / 100),
    };
    
    const custo_total = Object.values(custos).reduce((a, b) => a + b, 0);
    const receita_total = Object.values(receitas).reduce((a, b) => a + b, 0);
    const lucro_mensal = receita_total - custo_total;
    const margem = receita_total > 0 ? (lucro_mensal / receita_total) * 100 : 0;
    
    // Ponto de equilíbrio
    const receita_por_conta = receita_total / (inputs.contas_pf + inputs.contas_pj);
    const custo_fixo = custos.setup_mensal + custos.mensalidade;
    const custo_variavel_por_conta = (custo_total - custo_fixo) / (inputs.contas_pf + inputs.contas_pj);
    const ponto_equilibrio = receita_por_conta > custo_variavel_por_conta 
      ? Math.ceil(custo_fixo / (receita_por_conta - custo_variavel_por_conta))
      : 99999;
    
    const tempo_retorno = lucro_mensal > 0 ? Math.ceil(inputs.setup_provedor / lucro_mensal) : 999;
    
    setResults({
      custos,
      receitas,
      custo_total,
      receita_total,
      lucro_mensal,
      margem,
      ponto_equilibrio,
      tempo_retorno,
      viavel: lucro_mensal > 0,
    });
    
    setLoading(false);
  }, [inputs]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateViability();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [calculateViability]);

  const updateInput = (key, value) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-black" data-testid="viability-simulator-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-secondary transition-colors" data-testid="back-home">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Voltar</span>
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="hubaas" className="w-10 h-10 rounded-lg object-contain" />
              <h1 className="font-heading font-bold text-lg text-white">Simulador de Viabilidade</h1>
            </div>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid="nav-whatsapp">
                <MessageCircle className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Especialista</span>
              </Button>
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Inputs Panel */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Contas */}
            <Card className="bg-zinc-900 border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Users className="w-5 h-5 text-cyan-500" />
                  Contas e KYC
                </CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-6">
                <InputField
                  label="Contas Ativas PF"
                  value={inputs.contas_pf}
                  onChange={(v) => updateInput('contas_pf', v)}
                  min={0} max={100000} step={100}
                  tooltip="Quantidade de contas ativas de Pessoa Física"
                  testId="input-contas-pf"
                />
                <InputField
                  label="Contas Ativas PJ"
                  value={inputs.contas_pj}
                  onChange={(v) => updateInput('contas_pj', v)}
                  min={0} max={50000} step={100}
                  tooltip="Quantidade de contas ativas de Pessoa Jurídica"
                  testId="input-contas-pj"
                />
                <InputField
                  label="Custo KYC PF"
                  value={inputs.custo_kyc_pf}
                  onChange={(v) => updateInput('custo_kyc_pf', v)}
                  min={0} max={50} step={0.5}
                  prefix="R$"
                  tooltip="Custo para validação KYC de Pessoa Física"
                  testId="input-kyc-pf"
                />
                <InputField
                  label="Custo KYC PJ"
                  value={inputs.custo_kyc_pj}
                  onChange={(v) => updateInput('custo_kyc_pj', v)}
                  min={0} max={100} step={1}
                  prefix="R$"
                  tooltip="Custo para validação KYC de Pessoa Jurídica"
                  testId="input-kyc-pj"
                />
              </CardContent>
            </Card>

            {/* Boletos */}
            <Card className="bg-zinc-900 border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <FileText className="w-5 h-5 text-emerald-500" />
                  Boletos
                </CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-6">
                <InputField
                  label="Boletos Código de Barras"
                  value={inputs.boletos_codigo_barras}
                  onChange={(v) => updateInput('boletos_codigo_barras', v)}
                  min={0} max={500000} step={1000}
                  tooltip="Volume de boletos liquidados via código de barras"
                  testId="input-boletos-codigo"
                />
                <InputField
                  label="Custo por Boleto (Código)"
                  value={inputs.custo_boleto_codigo}
                  onChange={(v) => updateInput('custo_boleto_codigo', v)}
                  min={0} max={5} step={0.05}
                  prefix="R$"
                  testId="input-custo-boleto-codigo"
                />
                <InputField
                  label="Boletos QRCode PIX"
                  value={inputs.boletos_qrcode_pix}
                  onChange={(v) => updateInput('boletos_qrcode_pix', v)}
                  min={0} max={500000} step={1000}
                  tooltip="Volume de boletos liquidados via QRCode PIX"
                  testId="input-boletos-qrcode"
                />
                <InputField
                  label="Custo por Boleto (QRCode)"
                  value={inputs.custo_boleto_qrcode}
                  onChange={(v) => updateInput('custo_boleto_qrcode', v)}
                  min={0} max={5} step={0.05}
                  prefix="R$"
                  testId="input-custo-boleto-qrcode"
                />
                <InputField
                  label="Pagamentos de Boletos (Rebate)"
                  value={inputs.pagamentos_boletos}
                  onChange={(v) => updateInput('pagamentos_boletos', v)}
                  min={0} max={500000} step={1000}
                  tooltip="Volume para rebate de boletos arrecadados"
                  testId="input-pagamentos-boletos"
                />
                <InputField
                  label="Rebate por Boleto"
                  value={inputs.rebate_boleto}
                  onChange={(v) => updateInput('rebate_boleto', v)}
                  min={0} max={2} step={0.05}
                  prefix="R$"
                  testId="input-rebate-boleto"
                />
              </CardContent>
            </Card>

            {/* PIX e TED */}
            <Card className="bg-zinc-900 border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <QrCode className="w-5 h-5 text-purple-500" />
                  PIX e TED
                </CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-6">
                <InputField
                  label="Volume PIX In"
                  value={inputs.pix_in}
                  onChange={(v) => updateInput('pix_in', v)}
                  min={0} max={10000000} step={10000}
                  tooltip="Volume de recebimentos PIX"
                  testId="input-pix-in"
                />
                <InputField
                  label="Custo PIX In"
                  value={inputs.custo_pix_in}
                  onChange={(v) => updateInput('custo_pix_in', v)}
                  min={0} max={1} step={0.01}
                  prefix="R$"
                  testId="input-custo-pix-in"
                />
                <InputField
                  label="Volume PIX Out"
                  value={inputs.pix_out}
                  onChange={(v) => updateInput('pix_out', v)}
                  min={0} max={10000000} step={10000}
                  tooltip="Volume de envios PIX"
                  testId="input-pix-out"
                />
                <InputField
                  label="Custo PIX Out"
                  value={inputs.custo_pix_out}
                  onChange={(v) => updateInput('custo_pix_out', v)}
                  min={0} max={1} step={0.01}
                  prefix="R$"
                  testId="input-custo-pix-out"
                />
                <InputField
                  label="Volume TED In"
                  value={inputs.ted_in}
                  onChange={(v) => updateInput('ted_in', v)}
                  min={0} max={500000} step={1000}
                  testId="input-ted-in"
                />
                <InputField
                  label="Custo TED In"
                  value={inputs.custo_ted_in}
                  onChange={(v) => updateInput('custo_ted_in', v)}
                  min={0} max={5} step={0.1}
                  prefix="R$"
                  testId="input-custo-ted-in"
                />
                <InputField
                  label="Volume TED Out"
                  value={inputs.ted_out}
                  onChange={(v) => updateInput('ted_out', v)}
                  min={0} max={500000} step={1000}
                  testId="input-ted-out"
                />
                <InputField
                  label="Custo TED Out"
                  value={inputs.custo_ted_out}
                  onChange={(v) => updateInput('custo_ted_out', v)}
                  min={0} max={5} step={0.1}
                  prefix="R$"
                  testId="input-custo-ted-out"
                />
              </CardContent>
            </Card>

            {/* Cartões */}
            <Card className="bg-zinc-900 border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <CreditCard className="w-5 h-5 text-amber-500" />
                  Cartões
                </CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-6">
                <InputField
                  label="Volume Transações Cartão"
                  value={inputs.transacoes_cartao}
                  onChange={(v) => updateInput('transacoes_cartao', v)}
                  min={0} max={10000000} step={10000}
                  prefix="R$"
                  tooltip="Volume financeiro transacionado em cartões"
                  testId="input-transacoes-cartao"
                />
                <InputField
                  label="% MDR"
                  value={inputs.mdr_percentual}
                  onChange={(v) => updateInput('mdr_percentual', v)}
                  min={0} max={5} step={0.05}
                  suffix="%"
                  tooltip="Percentual sobre o valor da transação"
                  testId="input-mdr"
                />
                <InputField
                  label="Cartões Ativos"
                  value={inputs.cartoes_ativos}
                  onChange={(v) => updateInput('cartoes_ativos', v)}
                  min={0} max={100000} step={100}
                  testId="input-cartoes-ativos"
                />
                <InputField
                  label="Mensalidade Cartão Ativo"
                  value={inputs.mensalidade_cartao_ativo}
                  onChange={(v) => updateInput('mensalidade_cartao_ativo', v)}
                  min={0} max={20} step={0.5}
                  prefix="R$"
                  testId="input-mensalidade-cartao"
                />
              </CardContent>
            </Card>

            {/* Float e Crédito */}
            <Card className="bg-zinc-900 border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <PiggyBank className="w-5 h-5 text-teal-500" />
                  Float CDI e Crédito
                </CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-6">
                <InputField
                  label="Saldo Médio Parado"
                  value={inputs.saldo_medio_parado}
                  onChange={(v) => updateInput('saldo_medio_parado', v)}
                  min={0} max={100000000} step={100000}
                  prefix="R$"
                  tooltip="Saldo médio não transacionado"
                  testId="input-saldo-medio"
                />
                <InputField
                  label="% CDI (mensal)"
                  value={inputs.percentual_cdi}
                  onChange={(v) => updateInput('percentual_cdi', v)}
                  min={0} max={2} step={0.1}
                  suffix="%"
                  tooltip="Percentual do CDI para remuneração"
                  testId="input-cdi"
                />
                <InputField
                  label="Estimativa Crédito Emprestado"
                  value={inputs.estimativa_credito}
                  onChange={(v) => updateInput('estimativa_credito', v)}
                  min={0} max={50000000} step={100000}
                  prefix="R$"
                  tooltip="Volume de crédito concedido"
                  testId="input-credito"
                />
                <InputField
                  label="Spread Crédito"
                  value={inputs.spread_credito}
                  onChange={(v) => updateInput('spread_credito', v)}
                  min={0} max={5} step={0.1}
                  suffix="%"
                  tooltip="Margem sobre o crédito emprestado"
                  testId="input-spread-credito"
                />
              </CardContent>
            </Card>

            {/* Setup e Mensalidade */}
            <Card className="bg-zinc-900 border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Building2 className="w-5 h-5 text-orange-500" />
                  Setup e Mensalidade Provedor
                </CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-6">
                <InputField
                  label="Custo de Setup"
                  value={inputs.setup_provedor}
                  onChange={(v) => updateInput('setup_provedor', v)}
                  min={0} max={500000} step={1000}
                  prefix="R$"
                  tooltip="Custo inicial de implementação"
                  testId="input-setup"
                />
                <InputField
                  label="Mensalidade Provedor"
                  value={inputs.mensalidade_provedor}
                  onChange={(v) => updateInput('mensalidade_provedor', v)}
                  min={0} max={100000} step={500}
                  prefix="R$"
                  tooltip="Custo fixo mensal do provedor"
                  testId="input-mensalidade"
                />
              </CardContent>
            </Card>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className={`bg-zinc-900 border-2 ${results?.viavel ? 'border-green-600/50' : results ? 'border-red-600/50' : 'border-white/10'}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Calculator className="w-5 h-5 text-secondary" />
                    Resultado da Simulação
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin w-8 h-8 border-4 border-secondary border-t-transparent rounded-full" />
                    </div>
                  ) : results ? (
                    <>
                      {/* Viability Status */}
                      <div className={`p-4 rounded-xl ${results.viavel ? 'bg-green-900/30 border border-green-700/50' : 'bg-red-900/30 border border-red-700/50'}`} data-testid="viability-status">
                        <div className="flex items-center gap-3">
                          {results.viavel ? (
                            <CheckCircle2 className="w-8 h-8 text-green-500" />
                          ) : (
                            <XCircle className="w-8 h-8 text-red-500" />
                          )}
                          <div>
                            <p className={`font-bold text-lg ${results.viavel ? 'text-green-400' : 'text-red-400'}`}>
                              {results.viavel ? 'Projeto Viável!' : 'Projeto Inviável'}
                            </p>
                            <p className="text-sm text-slate-400">
                              Margem: {results.margem.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Key Metrics */}
                      <div className="space-y-3">
                        <ResultCard
                          icon={TrendingUp}
                          label="Receita Bruta Mensal"
                          value={formatCurrency(results.receita_total)}
                          color="green"
                          positive={true}
                          testId="result-receita-total"
                        />
                        <ResultCard
                          icon={TrendingDown}
                          label="Custo Total Mensal"
                          value={formatCurrency(results.custo_total)}
                          color="red"
                          positive={false}
                          testId="result-custo-total"
                        />
                        <ResultCard
                          icon={Wallet}
                          label="Lucro Mensal"
                          value={formatCurrency(results.lucro_mensal)}
                          color={results.lucro_mensal > 0 ? 'green' : 'red'}
                          testId="result-lucro-mensal"
                        />
                      </div>

                      {/* Breakeven */}
                      <div className="p-4 rounded-xl bg-zinc-800 border border-white/10" data-testid="result-breakeven">
                        <div className="flex items-center gap-3 mb-3">
                          <Users className="w-5 h-5 text-secondary" />
                          <span className="font-medium text-slate-300">Ponto de Equilíbrio</span>
                        </div>
                        <p className="text-2xl font-bold text-white">
                          {formatNumber(results.ponto_equilibrio)} contas
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          Mínimo para cobrir custos
                        </p>
                      </div>

                      {/* ROI */}
                      <div className="p-4 rounded-xl bg-zinc-800 border border-white/10" data-testid="result-roi">
                        <div className="flex items-center gap-3 mb-3">
                          <Clock className="w-5 h-5 text-secondary" />
                          <span className="font-medium text-slate-300">Retorno do Setup</span>
                        </div>
                        <p className="text-2xl font-bold text-white">
                          {results.tempo_retorno > 99 ? '+99' : results.tempo_retorno} meses
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          Para recuperar investimento
                        </p>
                      </div>

                      {/* Revenue Breakdown */}
                      <div className="pt-4 border-t border-white/10">
                        <p className="text-sm font-semibold text-slate-300 mb-3">Principais Receitas</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Float CDI</span>
                            <span className="font-medium text-white">{formatCurrency(results.receitas.float_cdi)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">PIX (In + Out)</span>
                            <span className="font-medium text-white">{formatCurrency(results.receitas.pix_in + results.receitas.pix_out)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Boletos</span>
                            <span className="font-medium text-white">{formatCurrency(results.receitas.boletos_codigo + results.receitas.boletos_qrcode + results.receitas.pagamentos_boletos)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">MDR Cartões</span>
                            <span className="font-medium text-white">{formatCurrency(results.receitas.mdr_cartao)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Crédito</span>
                            <span className="font-medium text-white">{formatCurrency(results.receitas.credito)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Cost Breakdown */}
                      <div className="pt-4 border-t border-white/10">
                        <p className="text-sm font-semibold text-slate-300 mb-3">Principais Custos</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-500">PIX (In + Out)</span>
                            <span className="font-medium text-white">{formatCurrency(results.custos.pix_in + results.custos.pix_out)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">KYC (PF + PJ)</span>
                            <span className="font-medium text-white">{formatCurrency(results.custos.kyc_pf + results.custos.kyc_pj)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Boletos</span>
                            <span className="font-medium text-white">{formatCurrency(results.custos.boletos_codigo + results.custos.boletos_qrcode)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Setup (mensal)</span>
                            <span className="font-medium text-white">{formatCurrency(results.custos.setup_mensal)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Mensalidade</span>
                            <span className="font-medium text-white">{formatCurrency(results.custos.mensalidade)}</span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Calculator className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Ajuste os parâmetros para ver os resultados</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="block mt-6">
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white py-6" data-testid="go-to-whatsapp">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Fale com um Especialista
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
